import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, Menu, X, Plus, Minus } from 'lucide-react';
import { Link } from 'react-router-dom';
const topics = [{
  id: 'signs',
  title: 'Signs your AC needs repair'
}, {
  id: 'prevention',
  title: 'AC breakdown prevention'
}, {
  id: 'energy',
  title: 'Energy saving with AC systems'
}, {
  id: 'hrv',
  title: 'Heat recovery ventilation guide'
}];
export function RepairsEfficiency() {
  const [activeId, setActiveId] = useState<string>('');
  const [isMobileTocOpen, setIsMobileTocOpen] = useState(false);
  const [openFaqId, setOpenFaqId] = useState<string | null>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveId(entry.target.id);
        }
      });
    }, {
      rootMargin: '-20% 0px -80% 0px'
    });
    topics.forEach(({
      id
    }) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });
    return () => observer.disconnect();
  }, []);
  const scrollTo = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 120;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setIsMobileTocOpen(false);
  };
  return <div className="min-h-screen bg-white font-sans text-primary-dark" data-id="element-1102">
      <section className="bg-[#E8F0F8] pt-32 pb-20" data-id="element-1103">
        <div className="container mx-auto px-6" data-id="element-1104">
          <div className="max-w-4xl" data-id="element-1105">
            <div className="flex items-center gap-2 text-sm font-medium text-primary-dark/60 mb-6" data-id="element-1106">
              <Link to="/knowledge-hub" className="hover:text-accent transition-colors" data-id="element-1107">
                Resources
              </Link>
              <ChevronRight className="w-4 h-4" data-id="element-1108" />
              <span className="text-primary-dark" data-id="element-1109">Repairs & Efficiency</span>
            </div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="text-4xl md:text-5xl lg:text-6xl font-serif leading-tight text-primary-dark mb-6" data-id="element-1110">
              AC repairs & energy efficiency
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="text-lg text-primary-dark/70 leading-relaxed" data-id="element-1111">
              Learn how to spot early warning signs, prevent costly breakdowns,
              and optimise your HVAC systems for maximum energy savings.
              Published April 2026.
            </motion.p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-6 py-16" data-id="element-1112">
        <div className="flex flex-col lg:flex-row gap-12" data-id="element-1113">
          <div className="lg:hidden" data-id="element-1114">
            <button onClick={() => setIsMobileTocOpen(!isMobileTocOpen)} className="flex items-center gap-2 text-primary-dark font-medium w-full justify-between bg-[#E8F0F8] p-4 rounded-xl" data-id="element-1115">
              <span data-id="element-1116">Table of contents</span>
              {isMobileTocOpen ? <X className="w-5 h-5" data-id="element-1117" /> : <Menu className="w-5 h-5" data-id="element-1118" />}
            </button>
            {isMobileTocOpen && <div className="mt-2 bg-white border border-primary-dark/10 rounded-xl p-4 shadow-lg" data-id="element-1119">
                <nav className="flex flex-col gap-3" data-id="element-1120">
                  {topics.map(topic => <button key={topic.id} onClick={() => scrollTo(topic.id)} className={`text-left text-sm font-medium transition-colors ${activeId === topic.id ? 'text-accent' : 'text-primary-dark/70 hover:text-primary-dark'}`} data-id="element-1121">
                      {topic.title}
                    </button>)}
                </nav>
              </div>}
          </div>

          <aside className="hidden lg:block w-64 shrink-0" data-id="element-1122">
            <div className="sticky top-32" data-id="element-1123">
              <h3 className="text-sm font-bold uppercase tracking-wider text-primary-dark/50 mb-6" data-id="element-1124">
                Contents
              </h3>
              <nav className="flex flex-col gap-4 border-l-2 border-primary-dark/10 pl-4" data-id="element-1125">
                {topics.map(topic => <button key={topic.id} onClick={() => scrollTo(topic.id)} className={`text-left text-sm font-medium transition-colors relative ${activeId === topic.id ? 'text-accent' : 'text-primary-dark/60 hover:text-primary-dark'}`} data-id="element-1126">
                    {activeId === topic.id && <motion.div layoutId="activeToc" className="absolute -left-[18px] top-1/2 -translate-y-1/2 w-0.5 h-full bg-accent" data-id="element-1127" />}
                    {topic.title}
                  </button>)}
              </nav>
            </div>
          </aside>

          <main className="flex-1 max-w-3xl prose prose-lg prose-headings:font-serif prose-headings:text-primary-dark prose-p:text-primary-dark/80 prose-a:text-accent prose-li:text-primary-dark/80" data-id="element-1128">
            {/* Topic 1 */}
            <section id="signs" className="mb-20 scroll-mt-32" data-id="element-1129">
              <h2 className="text-3xl font-serif mb-6" data-id="element-1130">
                Signs your AC needs repair
              </h2>
              <p data-id="element-1131">
                Ignoring early warning signs can turn a minor fix into a £1,000+
                compressor replacement. Look out for these 6 signs: no cool air,
                visible leaks, odd noises (grinding/squealing), sudden high
                bills, weak airflow, and foul smells.
              </p>

              <h3 className="text-2xl font-serif mt-8 mb-4" data-id="element-1132">
                Quick fixes vs professional repair
              </h3>
              <ul className="list-disc pl-6 mb-8 space-y-2" data-id="element-1133">
                <li data-id="element-1134">
                  <strong data-id="element-1135">Reset the breaker:</strong> Sometimes a simple reset
                  resolves electronic glitches.
                </li>
                <li data-id="element-1136">
                  <strong data-id="element-1137">Clean the filter:</strong> A temporary fix for weak
                  airflow.
                </li>
                <li data-id="element-1138">
                  <strong data-id="element-1139">Call a pro:</strong> For refrigerant leaks, electrical
                  issues, or mechanical noises.
                </li>
              </ul>

              <div className="bg-[#E8F0F8] p-8 rounded-2xl my-8" data-id="element-1140">
                <h4 className="text-xl font-serif mb-4" data-id="element-1141">
                  Need an emergency repair?
                </h4>
                <p className="mb-6 text-base" data-id="element-1142">
                  AccuTemp provides rapid response repairs across Coventry and
                  the West Midlands.
                </p>
                <Link to="/#contact" className="inline-flex items-center gap-2 bg-accent hover:bg-accent/90 text-white px-6 py-3 rounded-full text-sm font-medium transition-colors" data-id="element-1143">
                  Book repair now
                  <ChevronRight className="w-4 h-4" data-id="element-1144" />
                </Link>
              </div>

              <div itemScope itemType="https://schema.org/FAQPage" className="mt-12" data-id="element-1145">
                <h3 className="text-2xl font-serif mb-6" data-id="element-1146">Repair FAQs</h3>
                <div className="space-y-4" data-id="element-1147">
                  {[{
                  q: 'Do you offer emergency callouts?',
                  a: 'Yes, we provide 24-hour emergency response for our contracted commercial clients in Coventry.'
                }].map((faq, i) => <div key={i} itemScope itemProp="mainEntity" itemType="https://schema.org/Question" className="border border-primary-dark/10 rounded-xl overflow-hidden" data-id="element-1148">
                      <button onClick={() => setOpenFaqId(openFaqId === `signs-${i}` ? null : `signs-${i}`)} className="w-full px-6 py-4 flex items-center justify-between text-left bg-white hover:bg-primary-light/30 transition-colors" data-id="element-1149">
                        <span itemProp="name" className="font-medium text-primary-dark pr-8" data-id="element-1150">
                          {faq.q}
                        </span>
                        <span className="flex-shrink-0 text-primary-dark/40" data-id="element-1151">
                          {openFaqId === `signs-${i}` ? <Minus className="w-5 h-5" data-id="element-1152" /> : <Plus className="w-5 h-5" data-id="element-1153" />}
                        </span>
                      </button>
                      {openFaqId === `signs-${i}` && <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer" className="px-6 pb-4 pt-2 text-primary-dark/70 text-base border-t border-primary-dark/5" data-id="element-1154">
                          <p itemProp="text" data-id="element-1155">{faq.a}</p>
                        </div>}
                    </div>)}
                </div>
              </div>
            </section>

            {/* Topic 2 */}
            <section id="prevention" className="mb-20 scroll-mt-32" data-id="element-1156">
              <h2 className="text-3xl font-serif mb-6" data-id="element-1157">
                AC breakdown prevention
              </h2>
              <p data-id="element-1158">
                Up to 80% of commercial AC breakdowns are entirely preventable
                through regular servicing (typically 2x per year). The number
                one cause of failure is dirty filters restricting airflow, which
                causes the indoor coil to freeze and the compressor to overheat.
              </p>

              <div itemScope itemType="https://schema.org/FAQPage" className="mt-12" data-id="element-1159">
                <h3 className="text-2xl font-serif mb-6" data-id="element-1160">Prevention FAQs</h3>
                <div className="space-y-4" data-id="element-1161">
                  {[{
                  q: 'How much does a typical repair cost?',
                  a: 'Minor fixes start at a £100 minimum callout fee, but major component failures can cost thousands. Prevention is always cheaper.'
                }].map((faq, i) => <div key={i} itemScope itemProp="mainEntity" itemType="https://schema.org/Question" className="border border-primary-dark/10 rounded-xl overflow-hidden" data-id="element-1162">
                      <button onClick={() => setOpenFaqId(openFaqId === `prev-${i}` ? null : `prev-${i}`)} className="w-full px-6 py-4 flex items-center justify-between text-left bg-white hover:bg-primary-light/30 transition-colors" data-id="element-1163">
                        <span itemProp="name" className="font-medium text-primary-dark pr-8" data-id="element-1164">
                          {faq.q}
                        </span>
                        <span className="flex-shrink-0 text-primary-dark/40" data-id="element-1165">
                          {openFaqId === `prev-${i}` ? <Minus className="w-5 h-5" data-id="element-1166" /> : <Plus className="w-5 h-5" data-id="element-1167" />}
                        </span>
                      </button>
                      {openFaqId === `prev-${i}` && <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer" className="px-6 pb-4 pt-2 text-primary-dark/70 text-base border-t border-primary-dark/5" data-id="element-1168">
                          <p itemProp="text" data-id="element-1169">{faq.a}</p>
                        </div>}
                    </div>)}
                </div>
              </div>
            </section>

            {/* Topic 3 */}
            <section id="energy" className="mb-20 scroll-mt-32" data-id="element-1170">
              <h2 className="text-3xl font-serif mb-6" data-id="element-1171">
                Energy saving with AC systems
              </h2>
              <p data-id="element-1172">
                Commercial buildings can save up to 30% on HVAC energy costs
                through proper zoning, smart timers, and regular maintenance. A{' '}
                <Link to="/knowledge-hub/compliance" data-id="element-1173">TM44 inspection</Link> is
                an excellent tool for identifying specific areas of waste in
                your building.
              </p>
              <p data-id="element-1174">
                Quick tips: Set your cooling thermostat to 24°C in summer (not
                18°C), ensure annual deep cleaning of coils, and utilise heat
                recovery systems where possible.
              </p>

              <div itemScope itemType="https://schema.org/FAQPage" className="mt-12" data-id="element-1175">
                <h3 className="text-2xl font-serif mb-6" data-id="element-1176">Energy FAQs</h3>
                <div className="space-y-4" data-id="element-1177">
                  {[{
                  q: 'What is the ROI on energy-saving upgrades?',
                  a: 'Many businesses see a return on investment within 1-2 years through reduced energy bills alone.'
                }].map((faq, i) => <div key={i} itemScope itemProp="mainEntity" itemType="https://schema.org/Question" className="border border-primary-dark/10 rounded-xl overflow-hidden" data-id="element-1178">
                      <button onClick={() => setOpenFaqId(openFaqId === `energy-${i}` ? null : `energy-${i}`)} className="w-full px-6 py-4 flex items-center justify-between text-left bg-white hover:bg-primary-light/30 transition-colors" data-id="element-1179">
                        <span itemProp="name" className="font-medium text-primary-dark pr-8" data-id="element-1180">
                          {faq.q}
                        </span>
                        <span className="flex-shrink-0 text-primary-dark/40" data-id="element-1181">
                          {openFaqId === `energy-${i}` ? <Minus className="w-5 h-5" data-id="element-1182" /> : <Plus className="w-5 h-5" data-id="element-1183" />}
                        </span>
                      </button>
                      {openFaqId === `energy-${i}` && <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer" className="px-6 pb-4 pt-2 text-primary-dark/70 text-base border-t border-primary-dark/5" data-id="element-1184">
                          <p itemProp="text" data-id="element-1185">{faq.a}</p>
                        </div>}
                    </div>)}
                </div>
              </div>
            </section>

            {/* Topic 4 */}
            <section id="hrv" className="mb-20 scroll-mt-32" data-id="element-1186">
              <h2 className="text-3xl font-serif mb-6" data-id="element-1187">
                Heat recovery ventilation guide
              </h2>
              <p data-id="element-1188">
                Heat Recovery Ventilation (HRV) systems extract stale air from a
                building and use its heat to warm incoming fresh air. This
                process recovers up to 70% of heat that would otherwise be lost,
                drastically reducing heating costs while maintaining excellent
                indoor air quality.
              </p>

              <div itemScope itemType="https://schema.org/FAQPage" className="mt-12" data-id="element-1189">
                <h3 className="text-2xl font-serif mb-6" data-id="element-1190">HRV FAQs</h3>
                <div className="space-y-4" data-id="element-1191">
                  {[{
                  q: 'Are HRV systems suitable for factories?',
                  a: 'Yes, though they require specific filtration setups to handle dusty or contaminated industrial environments.'
                }].map((faq, i) => <div key={i} itemScope itemProp="mainEntity" itemType="https://schema.org/Question" className="border border-primary-dark/10 rounded-xl overflow-hidden" data-id="element-1192">
                      <button onClick={() => setOpenFaqId(openFaqId === `hrv-${i}` ? null : `hrv-${i}`)} className="w-full px-6 py-4 flex items-center justify-between text-left bg-white hover:bg-primary-light/30 transition-colors" data-id="element-1193">
                        <span itemProp="name" className="font-medium text-primary-dark pr-8" data-id="element-1194">
                          {faq.q}
                        </span>
                        <span className="flex-shrink-0 text-primary-dark/40" data-id="element-1195">
                          {openFaqId === `hrv-${i}` ? <Minus className="w-5 h-5" data-id="element-1196" /> : <Plus className="w-5 h-5" data-id="element-1197" />}
                        </span>
                      </button>
                      {openFaqId === `hrv-${i}` && <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer" className="px-6 pb-4 pt-2 text-primary-dark/70 text-base border-t border-primary-dark/5" data-id="element-1198">
                          <p itemProp="text" data-id="element-1199">{faq.a}</p>
                        </div>}
                    </div>)}
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>
    </div>;
}